import { Field } from 'redux-form';
import Overlay from 'praece-react-bootstrap/lib/Overlay';
import Pikaday from 'pikaday';
import Popover from 'praece-react-bootstrap/lib/Popover';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';

import { CancelButton } from 'components/widgets/buttons';
import {
  CheckboxInput,
  NumberInput,
  SelectInput,
  SingleRadioInput
} from 'components/forms/form-elements';
import { DAYSOFWEEK, MONTHS } from 'constants';
import { Row, SimpleReduxForm } from 'components/forms/form-structure';
import { getNextRecurrenceDate } from 'utils/form-utils';
import { longDate } from 'utils/format-utils';
import { stopPropagation, preventDefault } from 'utils/event-handlers';
import SVG from 'components/widgets/icons/svg';

const dayOffsetOptions = [
  { label: 'First', value: '0' },
  { label: 'Second', value: '1' },
  { label: 'Third', value: '2' },
  { label: 'Fourth', value: '3' }
];

export default class DatePicker extends React.Component {
  static propTypes = {
    selectDate: PropTypes.func.isRequired,
    children: PropTypes.node,
    startDateLabel: PropTypes.string,
    endDateLabel: PropTypes.string,
    startDate: PropTypes.any,
    onEntering: PropTypes.func,
    container: PropTypes.any,
    endDate: PropTypes.any,
    task: PropTypes.object,
    recurrence: PropTypes.object
  };

  constructor() {
    super();

    this.state = { showPopover: false, setting: 'endDate' };

    this.initOverlay = this.initOverlay.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.removeStartDate = this.removeStartDate.bind(this);
    this.saveRecurrence = this.saveRecurrence.bind(this);
    this.show = () => this.setState({ showPopover: !this.state.showPopover });
    this.hide = () => this.setState({ showPopover: false });
    this.destroy = () => this.state.picker.destroy();
  }

  initOverlay(overlay) {
    const { endDate, startDate } = this.props;
    const start = moment(startDate).toDate();
    const end = moment(endDate).toDate();

    const config = {
      onSelect: this.handleSelect,
      showDaysInNextAndPreviousMonths: true,
      enableSelectionDaysInNextAndPreviousMonths: true,
      defaultDate: end,
      setDefaultDate: true,
      keyboardInput: false
    };

    if (this.props.startDate) {
      config.startRange = start;
      config.endRange = end;
      config.minDate = start;
    }

    const picker = new Pikaday(config);
    
    document.removeEventListener('keydown', picker._onKeyChange);
    overlay.getElementsByClassName('picker')[0].appendChild(picker.el);

    this.setState({ picker, setting: 'endDate' });
  }

  handleSelect(date) {
    const { selectDate, endDate, startDate, recurrence } = this.props;
    const { setting, picker } = this.state;
    const updates = { [setting]: date };
    
    if (setting === 'endDate') {
      if (!startDate && !_.get(recurrence, 'enabled')) this.hide();

      if (startDate) {
        // picker.setDate(date, true);
        picker.setStartRange(moment(startDate).toDate());
        picker.setEndRange(date);
        picker.draw();
      }
    }

    if (setting === 'startDate' && date) {
      picker.setStartRange(date);
      picker.setMinDate(date);

      // If the start date is above the end date reset the end date
      if (moment(date).isAfter(endDate)) {
        updates.endDate = date;
        picker.setDate(date, true);
        picker.setEndRange(date);
      } else {
        picker.setEndRange(moment(endDate).toDate());
      }

      picker.draw();
      this.setState({ setting: 'endDate' });
    }

    selectDate(updates);
  }

  removeStartDate() {
    const { selectDate } = this.props;

    this.state.picker.setStartRange(null);
    this.state.picker.setEndRange(null);
    this.state.picker.setMinDate(null);
    this.setState({ setting: 'endDate' });
    selectDate({ startDate: null });
  }

  saveRecurrence(rules) {
    const { recurrence, task } = this.props;

    if (!rules.frequency || rules.frequency < 1) delete rules.frequency;
    if (!rules.date || rules.date < 1 || rules.date > 31) delete rules.date;
    if (rules.date > moment().month(rules.month || 0).daysInMonth()) rules.date = moment().month(rules.month || 0).daysInMonth();

    const update = _.assign({
      enabled: true,
      interval: 'week',
      monthType: 'byDate',
      frequency: 1,
      dayOffset: 0,
      skipWeekends: true,
      month: moment(task.dueDate).month(),
      date: moment(task.dueDate).date(),
      day: moment(task.dueDate).day()
    }, recurrence, rules);

    this.props.selectDate({ recurrence: update });
  }

  render() {
    const { children, startDateLabel, endDateLabel, onEntering, startDate, recurrence, task, ...props } = this.props;
    const { showPopover, setting } = this.state;
    const passed = _.omit(props, ['selectDate', 'startDate', 'endDate']);
    const hasStartDate = startDate || setting === 'startDate';
    const rules = recurrence || {};
    const daysInMonth = moment().month(rules.month || 0).daysInMonth();
    const hasWeekends = rules.interval === 'day' || (_.includes(['month', 'year'], rules.interval) && rules.monthType === 'byDate');

    const header = (
      <div className="w-100 d-flex">
        <span className="p-1">Select Date</span>
        <SVG onClick={this.hide} className="close" src="svg/close.svg" />
      </div>
    );

    return (
      <div {...passed} onClick={stopPropagation()}>
        <div onClick={stopPropagation(preventDefault(this.show))}>{children}</div>
        <Overlay
          shouldUpdatePosition
          onEnter={this.initOverlay}
          rootClose
          placement="bottom"
          target={this}
          onHide={this.hide}
          onEntering={onEntering}
          onExited={this.destroy}
          show={showPopover}
        >
          <Popover
            id="date-window"
            title={header}
            className={`setting-${_.kebabCase(setting)}`}
            onClick={stopPropagation()}
            onMouseOver={stopPropagation()}
          >
            <div className="date-type-toggle">
              <div className="date-type-toggle-header">
                <SVG src="svg/calendar.svg" />
                <span>
                  {setting === 'startDate' && startDateLabel}
                  {setting === 'endDate' && endDateLabel}
                </span>
                <div className="calendar-buttons btn-group">
                  { !hasStartDate && <CancelButton onClick={() => this.setState({ setting: 'startDate' })}>Add Start Date</CancelButton> }
                  { hasStartDate && <CancelButton onClick={() => this.removeStartDate()}>Clear Start Date</CancelButton> }
                </div>
              </div>
              <div className="picker"></div>
            </div>
            <div className="recurrence-toggle">
              <div className="date-type-toggle-header">
                <SVG src="svg/refresh.svg" />
                <div className="calendar-buttons btn-group">
                  { !rules.enabled && <CancelButton onClick={() => this.saveRecurrence({ enabled: true })}>Make Recurring</CancelButton> }
                  { rules.enabled && <CancelButton onClick={() => this.saveRecurrence({ enabled: false })}>Stop Recurring</CancelButton> }
                </div>
              </div>
              { rules.enabled &&
                <div>
                  <SimpleReduxForm
                    form={`date-picker-task-${task.id}`}
                    onChange={this.saveRecurrence}
                    preventAutoFocus
                    initialValues={rules}
                    customSubmitArea
                  >
                    <Row label="Task will recur every:">
                      <Field component={NumberInput} name="frequency" min="1" />
                      <Field
                        name="interval"
                        component={SelectInput}
                        clearable={false}
                        options={[
                          { label: pluralize('Day', Number(rules.frequency)), value: 'day' },
                          { label: pluralize('Week', Number(rules.frequency)), value: 'week' },
                          { label: pluralize('Month', Number(rules.frequency)), value: 'month' },
                          { label: pluralize('Year', Number(rules.frequency)), value: 'year' }
                        ]}
                      />
                    </Row>

                    { rules.interval === 'week' &&
                      <Field name="day" label="On:" component={SelectInput} clearable={false} options={DAYSOFWEEK} />
                    }

                    { rules.interval === 'month' &&
                      <div className="month-type">
                        <label className="form-label">On:</label>
                        <div className="flex month-by-date">
                          <Field component={SingleRadioInput} name="monthType" checkedValue="byDate" />
                          <Field disabled={rules.monthType !== 'byDate'} addonLeft="Day" component={NumberInput} name="date" min="1" max="31" />
                        </div>
                        <div className="flex month-by-day">
                          <Field component={SingleRadioInput} name="monthType" checkedValue="byDay" />
                          <Row className="flex-grow">
                            <Field disabled={rules.monthType !== 'byDay'} component={SelectInput} name="dayOffset" clearable={false} options={dayOffsetOptions} />
                            <Field disabled={rules.monthType !== 'byDay'} name="day" component={SelectInput} clearable={false} options={DAYSOFWEEK} />
                          </Row>
                        </div>
                      </div>
                    }

                    { rules.interval === 'year' &&
                      <div className="month-type">
                        <label className="form-label">On:</label>
                        <Field component={SelectInput} name="month" clearable={false} options={MONTHS} />
                        <div className="flex month-by-date">
                          <Field component={SingleRadioInput} name="monthType" checkedValue="byDate" />
                          <Field disabled={rules.monthType !== 'byDate'} addonLeft="Day" component={NumberInput} name="date" min="1" max={daysInMonth} />
                        </div>
                        <div className="flex month-by-day">
                          <Field component={SingleRadioInput} name="monthType" checkedValue="byDay" />
                          <Row className="flex-grow">
                            <Field disabled={rules.monthType !== 'byDay'} component={SelectInput} name="dayOffset" clearable={false} options={dayOffsetOptions} />
                            <Field disabled={rules.monthType !== 'byDay'} name="day" component={SelectInput} clearable={false} options={DAYSOFWEEK} />
                          </Row>
                        </div>
                      </div>
                    }

                    { hasWeekends &&
                      <Field component={CheckboxInput} name="skipWeekends" label="Skip weekends - if the task falls on Saturday or Sunday it will be due the following Monday" />
                    }
                  </SimpleReduxForm>
                  <p className="center-align">If you closed this task on its current due date it would recur on <span className="emphasize">{longDate(getNextRecurrenceDate(rules, task))}</span>.</p>
                </div>
              }
            </div>
          </Popover>
        </Overlay>
      </div>
    );
  }
}

